import { ChevronLeft, ChevronRight, Info } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import carbonGreen from '@/assets/images/carbon-simulation/carbon-green.png'
import carbonMountain from '@/assets/images/carbon-simulation/carbon-mountain.png'
import carbonMountainCloud from '@/assets/images/carbon-simulation/carbon-mountain-cloud.png'
import { Button } from '@/components/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTrigger,
} from '@/components/dialog'
import { CarbonIndicators } from '@/models/simulation'
import api from '@/services/api'

import { DownloadButton } from '../../download-button'
import { CarbonMapCard } from './carbon-map-card'
import { AvoidedConservationEmissions } from './components/avoided-conservation-emissions'
import { BiomassAndCarbon } from './components/biomass-and-carbon'
import { GenerationCreditCarbon } from './components/generation-credit-carbon'
import { RiskOfDeforestationWithoutConservation } from './components/risk-of-deforestation-without-conservation'

interface CarbonData {
  indicadores: CarbonIndicators
  imagens: {
    mapa_calor_biomassa: string
    mapa_calor_biomassa_leve: string
  }
  data_coleta: string
  evidencias_disponiveis?: string[]
  area_preservada?: string
  periodo?: string
}

export function CarbonTab() {
  const { idProperty } = useParams()
  const [carbonData, setCarbonData] = useState<CarbonData | null>(null)
  const [currentQuarter, setCurrentQuarter] = useState('')
  const [selectedPeriod, setSelectedPeriod] = useState<'marco' | 'trimestral'>(
    'marco',
  )

  const getDataCarbonFromGroundZero = useCallback(async () => {
    await api
      .get<CarbonData>(`/property/carbon/marco-zero/data/${idProperty}`)
      .then(({ data }) => {
        const evidenceData = data.evidencias_disponiveis![0]

        setCarbonData({
          ...data,
          indicadores: data.indicadores,
        })
        setCurrentQuarter(evidenceData)
      })
      .catch((error) => {
        if (error.response.status === 404) {
          return
        }

        return toast.error(error.response.data.message)
      })
  }, [idProperty])

  const getQuarterlyDataCarbon = useCallback(async () => {
    await api
      .get(`/property/carbon/data/${idProperty}/${currentQuarter}`)
      .then(({ data }) => {
        setCarbonData((prev) => ({
          ...data,
          area_preservada: prev?.area_preservada,
          evidencias_disponiveis: prev?.evidencias_disponiveis,
          indicadores: data.indicadores,
        }))
      })
  }, [currentQuarter, idProperty])

  useEffect(() => {
    if (selectedPeriod === 'trimestral') {
      getQuarterlyDataCarbon()
    }
  }, [getQuarterlyDataCarbon, selectedPeriod])

  useEffect(() => {
    if (selectedPeriod === 'marco') {
      getDataCarbonFromGroundZero()
    }
  }, [getDataCarbonFromGroundZero, selectedPeriod])

  function handleQuarterChange(direction: 'next' | 'prev') {
    if (!carbonData?.evidencias_disponiveis?.length) {
      return
    }

    const currentIndex =
      carbonData?.evidencias_disponiveis?.indexOf(currentQuarter)

    const newIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1

    return setCurrentQuarter(carbonData.evidencias_disponiveis[newIndex])
  }

  function disabledQuarterChange(direction: 'next' | 'prev') {
    if (!carbonData?.evidencias_disponiveis?.length) {
      return
    }

    const currentIndex =
      carbonData?.evidencias_disponiveis.indexOf(currentQuarter)

    if (direction === 'next') {
      return currentIndex >= carbonData.evidencias_disponiveis.length - 1
    }

    return currentIndex <= 0
  }

  const isDataAvailable = carbonData

  return (
    <div className="space-y-2">
      <div className="space-y-6 text-sm lg:*:text-lg">
        <Dialog>
          <p>
            Este relatório foi elaborado com base na metodologia TREES v2.0 (The
            REDD+ Environmental Excellence Standard), desenvolvida no âmbito da
            iniciativa ART (Architecture for REDD+ Transactions). O TREES
            estabelece critérios rigorosos para quantificação, monitoramento e
            verificação de emissões e remoções de GEE, assegurando a integridade
            ambiental dos créditos gerados e possibilitando sua utilização em
            relatórios de ESG.
            <DialogTrigger>
              <Info className="text-bios-primary hover:text-bios-secondary -mb-0.5 ml-2 size-3 cursor-pointer rounded-full lg:size-5" />
            </DialogTrigger>
          </p>

          <DialogContent className="lg-py-0 h-auto py-10">
            <DialogDescription className="text-monochromatic-500 space-y-4">
              <p className="lg:paragraph">
                Os relatórios relativos às políticas de ESG permitem incluir os
                dados auferidos nas métricas contábeis estabelecidas pelos
                padrões do ISSB, tais como o Protocolo GHG, que fornece a
                métrica fundamental para as emissões. O Trees 2.0 (UNFCCC)
                assegura a credibilidade dos resultados climáticos específicos
                das florestas. O ISSB demanda que as empresas integrem essas
                informações – tanto os dados de emissões/remoções (GHG) quanto
                os riscos/oportunidades associados a ativos florestais (onde o
                Trees 2.0 agrega confiança) – em seus relatórios financeiros
                globais. Juntos, formam uma aliança poderosa para uma
                contabilidade climática mais completa.
              </p>
            </DialogDescription>
          </DialogContent>
        </Dialog>

        <p>
          Como referência para o cálculo do baseline de desmatamento, adotamos o
          modelo de mudança de cobertura florestal proposto por Hansen et al.,
          reconhecido internacionalmente por sua acurácia e aplicabilidade
          espacial. Essa abordagem garante que os resultados apresentados sejam
          robustos, auditáveis e alinhados aos padrões globais de qualidade para
          créditos de carbono.
        </p>
      </div>

      {isDataAvailable ? (
        <div className="flex flex-col gap-16">
          <div className="flex flex-col-reverse gap-8 lg:mt-10 lg:flex-row lg:items-end">
            <div className="text-alert-success flex flex-1 flex-col items-center gap-8 text-xs font-bold lg:flex-row lg:gap-20 lg:text-base">
              <div className="flex flex-col gap-6 rounded-2xl p-5 shadow-xl">
                <div className="flex items-center justify-center gap-10 *:flex *:gap-2">
                  <div>
                    <input
                      id="marco"
                      onChange={() => setSelectedPeriod('marco')}
                      checked={selectedPeriod === 'marco'}
                      name="carbonPeriod"
                      value="marco"
                      type="radio"
                    />
                    <label htmlFor="marco">Marco zero</label>
                  </div>
                  <div>
                    <input
                      id="trimestral"
                      disabled={!carbonData?.evidencias_disponiveis?.length}
                      onChange={() => setSelectedPeriod('trimestral')}
                      checked={selectedPeriod === 'trimestral'}
                      name="carbonPeriod"
                      value="trimestral"
                      type="radio"
                    />
                    <label htmlFor="trimestral">Periódico</label>
                  </div>
                </div>

                {selectedPeriod === 'trimestral' && (
                  <div>
                    <div className="flex w-full items-center justify-center gap-3">
                      <Button
                        variant="ghost"
                        disabled={
                          selectedPeriod !== 'trimestral' ||
                          disabledQuarterChange('prev')
                        }
                        className="bg-monochromatic-200 text-bios-primary disabled:bg-monochromatic-200 disabled:text-monochromatic-300 h-8! w-8! rounded-sm border-none p-0 shadow-md"
                      >
                        <ChevronLeft
                          onClick={() => handleQuarterChange('prev')}
                        />
                      </Button>

                      <p className="text-accordion-title text-xl text-nowrap">
                        {carbonData?.periodo}
                      </p>

                      <Button
                        variant="ghost"
                        disabled={
                          selectedPeriod !== 'trimestral' ||
                          disabledQuarterChange('next')
                        }
                        className="bg-monochromatic-200 text-bios-primary disabled:bg-monochromatic-200 disabled:text-monochromatic-300 h-8! w-8! rounded-sm border-none p-0 shadow-md"
                      >
                        <ChevronRight
                          onClick={() => handleQuarterChange('next')}
                        />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <DownloadButton
              className="ml-auto"
              downloadRoute={''}
              testId="property-download-carbon-data-button"
            />
          </div>

          <div className="flex flex-col gap-10">
            <div className="flex w-full flex-col justify-between gap-10 lg:flex-row">
              {carbonData?.imagens && (
                <CarbonMapCard
                  img={{
                    highRes: carbonData.imagens.mapa_calor_biomassa,
                    lowRes: carbonData.imagens.mapa_calor_biomassa_leve,
                  }}
                />
              )}

              <div className="bg-bios-yellow-200 h-124 rounded-2xl shadow-xl lg:w-1/4">
                <div className="relative flex h-3/5 items-end">
                  <img
                    className="absolute ml-2 w-4/5 select-none"
                    src={carbonMountain}
                    alt=""
                  />
                  <img
                    className="absolute top-6 right-6 select-none"
                    src={carbonMountainCloud}
                    alt=""
                  />
                </div>
                <div className="from-bios-green-800 to-bios-green-400 flex h-2/5 flex-col justify-center rounded-2xl bg-linear-to-r text-center text-white">
                  <p className="text-xl font-semibold">
                    Área total da propriedade:
                  </p>
                  <span className="text-4xl font-bold">
                    {carbonData.area_preservada}ha
                  </span>
                </div>
              </div>

              <div className="bg-bios-yellow-200 h-124 rounded-3xl shadow-xl lg:w-1/4">
                <div className="flex h-3/5 items-center justify-center">
                  <img src={carbonGreen} className="w-3/5" alt="" />
                </div>
                <div className="from-bios-green-950 to-bios-green-1000 flex h-2/5 flex-col justify-center rounded-3xl bg-linear-to-r text-center text-white">
                  <p className="text-xl font-semibold">
                    Área do projeto (ROI):
                  </p>
                  <span className="text-4xl font-bold">
                    {carbonData.indicadores.project_area_roi}ha
                  </span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-6 place-items-center items-center justify-center gap-16 lg:grid-cols-6">
              <AvoidedConservationEmissions
                co2EmissionsAvoidedNoDeductions={
                  carbonData.indicadores.co2_emissions_avoided_no_deductions
                }
                leakageDeduction={carbonData.indicadores.leakage_deduction}
                nonPermanenceDeduction={
                  carbonData.indicadores.non_permanence_deduction
                }
                spontaneousEmissions={
                  carbonData.indicadores.spontaneous_emissions
                }
                projectEmissions={carbonData.indicadores.project_emissions}
              />

              <BiomassAndCarbon
                abovegroundBiomass={carbonData.indicadores.aboveground_biomass}
                abovegroundCarbonStock={
                  carbonData.indicadores.aboveground_carbon_stock
                }
                belowGroundBiomass={carbonData.indicadores.belowground_biomass}
                belowGroundCarbonStock={
                  carbonData.indicadores.belowground_carbon_stock
                }
                totalBiomass={carbonData.indicadores.total_biomass}
                totalCarbonStock={carbonData.indicadores.total_carbon_stock}
                totalCo2Equivalent={carbonData.indicadores.total_co2_equivalent}
              />

              <RiskOfDeforestationWithoutConservation
                deforestationCarbonReleaseFactor={
                  carbonData.indicadores.deforestation_carbon_release_factor
                }
                expectedDeforestationRate={
                  carbonData.indicadores.expected_deforestation_rate
                }
                projectAreaWithoutConservation={
                  carbonData.indicadores.project_area_without_conservation
                }
                totalCo2ReleasedByDeforestation={
                  carbonData.indicadores.total_co2_released_by_deforestation
                }
              />

              <GenerationCreditCarbon
                creditsGenerated={
                  carbonData.indicadores.carbon_credits_generated
                }
                creditsGeneratedPerHa={
                  carbonData.indicadores.carbon_credits_generated_per_ha
                }
              />
            </div>
          </div>

          <div className="lg:w-4/5">
            <span className="text-sm lg:text-base">
              <strong>Observações:</strong> Todas as abordagens adotadas pela
              6BIOS são tecnicamente justificadas em publicação disponível para
              consulta. Nossos cálculos dos créditos de carbono são
              transparentes e auditáveis. Nossos projetos nunca reivindicam os
              créditos por sequestro líquido da floresta conservada.
            </span>
          </div>
        </div>
      ) : (
        <h3 className="text-monochromatic-400 text-center text-base lg:text-start">
          Sem dados de carbono
        </h3>
      )}
    </div>
  )
}
